// THE NEEDS FORM — what a blocker asks a human (or a human's agent) to supply,
// as a form spec: `{ version: 2, fields[], landed[] }` (epic #3756, one-way door 4).
// Stored in `blockers.needs` (jsonb); re-exported through `../blocker.ts`.
//
// WHY A FORM AND NOT A KEY LIST. v1 was `{ env_keys, environment }`: env vars
// only, one environment per ask. Onboarding asks for everything a repo's work
// needs in ONE link — env secrets, the URLs of the services `bin/dev` reaches,
// MCP servers, OAuth connections — so one ask spans kinds and environments, and
// the one-link form (`/blocker-fix/<jwt>`) and the machine path
// (`GET /v1/blockers/:id/spec`) both render one input per FIELD.
//
// v1 IS DELETED, NOT PARSED. 0 prod rows carried `needs` when v2 landed (epic
// #3756, census 2026-09-11), so a v1 arm would be a branch nothing ever reads —
// untested by construction. A v1 value fails {@link BlockerNeedsSchema} loudly.
//
// NAMES, NEVER VALUES — and that includes `landed`. `blockers.needs` is plaintext
// jsonb, projected on every `blocker list` and composed into the raw task an
// answer mints: a value written here is durable in three places and cannot be
// unsent. `landed` records WHICH fields a human supplied, by name; the value went
// to its store (the repo vault, the integrations table) and never came here.
//
// ONE NAME PER FIELD, AND ITS NAMESPACE. {@link needsFieldName} is a field's
// identity — the form's input name, the `landed` entry, and one of the names the
// fix link's `fields_hash` covers. The two VAULT kinds (`env_secret`,
// `service_url`) use the bare env-var name, because both land in the same
// `repo_secrets` namespace and a clash between them is a real clash. The other two
// are prefixed `mcp:` and `oauth:`: they land elsewhere, and a repo's `sentry` MCP
// server, the account's Sentry OAuth connection and an env var called `sentry`
// are three different asks. A POSIX env-var name can never contain `:`, so a
// prefixed name can never collide with a vault name.

import { z } from 'zod';
import { vetPublicHttpUrl } from '../../value-objects/host-vetting';
import {
  RepoSecretNameSchema,
  SecretEnvironment,
  SecretEnvironmentSchema,
  VAULT_MAX_ENTRIES,
} from '../../value-objects/project-ingress';
import { secretShapesInJson } from '../../value-objects/redaction';
import { IntegrationKind } from '../integration';

/** The stored shape's version. A literal: the day a v3 exists, v2 rows are a migration. */
export const BLOCKER_NEEDS_VERSION = 2;

/**
 * How many FIELDS one blocker may ask for.
 *
 * SIXTEEN. It predates v2 (it bounded `env_keys`) and is kept deliberately; what
 * holds it up changed, so here is what does now:
 *
 *  1. **It must never exceed {@link VAULT_MAX_ENTRIES} (64).** Every vault field
 *     of one ask can be submitted as ONE `PUT /v1/repos/:owner/:repo/secrets`
 *     batch, and that route refuses a batch over `VAULT_MAX_ENTRIES`; a wider
 *     ceiling here would let the platform generate a form its own vault route
 *     rejects. Pinned by `needs-form.test.ts`, because the two constants live in
 *     different files.
 *  2. **The row is read in pages.** `listBlockers` parses a whole page of rows at
 *     once and projects `needs` on each, so the per-row bound is what keeps a page
 *     bounded. Every field member below is bounded too.
 *  3. **Widening later is free; narrowing later is not.** A read shape that grows
 *     leaves every stored row readable; one that shrinks makes the rows above the
 *     new bound unreadable (and one bad row fails `listBlockers` for the whole
 *     account). So the pre-existing value stays until a real ask needs more.
 *
 * What no longer holds it up: v1's link carried every NAME in its signed payload,
 * and 16 was the widest set whose JWS fit the 4096-char route param. The v2 link
 * carries a fixed-size hash (`value-objects/blocker-fix-link.ts` `fields_hash`), so
 * the link is no longer a bound at all.
 *
 * A repo that genuinely needs more than sixteen things from a human is not one
 * form; it is two asks, or the vault page.
 */
export const BLOCKER_NEEDS_MAX_FIELDS = 16;

/** A field's one line of "why" in front of a human — a subject, never a dump (`BLOCKER_SUMMARY_MAX`). */
export const NEEDS_FIELD_DESCRIPTION_MAX = 200;
/** OAuth scopes one field may request. Bounded for the page reason in {@link BLOCKER_NEEDS_MAX_FIELDS}. */
export const NEEDS_OAUTH_SCOPES_MAX = 32;
/** An MCP server URL. The row is plaintext and paged; a URL longer than this is not an endpoint. */
export const NEEDS_MCP_URL_MAX = 2_048;

/**
 * The field kinds, closed. Stored in jsonb and Zod-at-read, so this union is a
 * ONE-WAY DOOR: a member added later is a read widening (free), a member removed
 * later is unreadable rows.
 */
export const NeedsFieldKind = {
  /** A secret env var, sealed into the repo vault for one environment. */
  EnvSecret: 'env_secret',
  /** The URL of a service the work reaches (`DATABASE_URL`) — an env var whose value is a URL. */
  ServiceUrl: 'service_url',
  /** An MCP server the repo's agents use — mirrors `.maintainer.yml` `mcp_servers`. */
  McpServer: 'mcp_server',
  /** An account-level OAuth connection. Completed in a dashboard session, never by a machine. */
  OauthConnection: 'oauth_connection',
} as const;
export type NeedsFieldKind = (typeof NeedsFieldKind)[keyof typeof NeedsFieldKind];

/**
 * The providers an `oauth_connection` field may name: the OAuth-connectable
 * members of {@link IntegrationKind}, so the form's Connect button always has a
 * connector behind it. `s3` is not OAuth; MCP-server OAuth is its own later slice
 * (epic #3756, door 7).
 */
export const NEEDS_OAUTH_PROVIDERS = [IntegrationKind.Sentry, IntegrationKind.Slack] as const;
export type NeedsOauthProvider = (typeof NEEDS_OAUTH_PROVIDERS)[number];

/**
 * A REFERENCE NAME, and the ONE definition of it: lowercase, digits, `_` or `-`, with
 * an optional single `ns:name` colon segment (the namespaced handles — `integration:…`,
 * `dashboard:…` — #598).
 *
 * It lives HERE and `packages/policy` imports it, the direction every shared vocabulary
 * already runs (`ReviewDepth`, `ToolServiceSlugSchema`; `policy/src/validate.ts` calls
 * that the house pattern): `@developerz/domain` imports no internal package, so the
 * reverse is impossible, and two copies of one regex is a rule that can start refusing
 * different names on either side of the same field. An MCP field's `name` and
 * `token_ref` become `.maintainer.yml`'s `mcp_servers[].name` / `.token_ref`, so a name
 * the policy would refuse must not be askable here — which is only true while the two
 * are the same object.
 */
export const RefNameSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9][a-z0-9_-]*(?::[a-z0-9][a-z0-9_-]*)?$/,
    'lowercase, digits, _ or - (optional single ns:name colon)',
  );

/**
 * The MCP transports, and the ONE definition of them — `.maintainer.yml`'s
 * `mcp_servers[].transport` is `z.enum` over this same object
 * (`packages/policy/src/schema.ts`), for the reason on {@link RefNameSchema}.
 */
export const McpTransport = {
  Sse: 'sse',
  HttpStreamable: 'http-streamable',
  Stdio: 'stdio',
} as const;

export type McpTransport = (typeof McpTransport)[keyof typeof McpTransport];

/** The transports as a tuple, for the `z.enum` and the form's radio list. */
export const NEEDS_MCP_TRANSPORTS = [
  McpTransport.Sse,
  McpTransport.HttpStreamable,
  McpTransport.Stdio,
] as const;

const descriptionSchema = z.string().trim().min(1).max(NEEDS_FIELD_DESCRIPTION_MAX).optional();

/**
 * The environments a blocker may ASK a human to fill. `dev` and `ci`. **Never
 * `prod`**, and the reason is delivery, not danger.
 *
 * `secretEnvironmentForTaskClass` maps `ci → ci` and `interactive | coding |
 * gardener → dev`, and has **no `prod` branch at all — that absence IS the
 * policy**. A `prod` row is storable and is never sealed onto any fleet
 * assignment. So a `prod` ask would run the whole chain and still fail at the
 * end: a human types a live production credential into a form, we store it,
 * audit it, and the run that was blocked still cannot see the value. That is
 * strictly worse than refusing, because it is silent and it has cost the
 * customer a real credential.
 */
export const BLOCKER_ASKABLE_ENVIRONMENTS = [SecretEnvironment.Dev, SecretEnvironment.Ci] as const;
export type BlockerAskableEnvironment = (typeof BLOCKER_ASKABLE_ENVIRONMENTS)[number];

/**
 * Narrow a STORED environment to one a blocker may ask about. A type guard rather
 * than an inline `includes`: the read shape still admits `prod` on purpose
 * (narrowing a read is data loss), so every consumer acting on a stored field
 * crosses from the wide type to the narrow one, and one crossing is checkable.
 */
export function isBlockerAskableEnvironment(
  environment: SecretEnvironment,
): environment is BlockerAskableEnvironment {
  return (BLOCKER_ASKABLE_ENVIRONMENTS as readonly SecretEnvironment[]).includes(environment);
}

// ─── the fields, READ shape ──────────────────────────────────────────────────

/** A vault field's members. `name` uses {@link RepoSecretNameSchema}: the exact rule `repo_secrets_name_ck` enforces. */
const vaultFieldMembers = {
  name: RepoSecretNameSchema,
  environment: SecretEnvironmentSchema,
  description: descriptionSchema,
};

export const EnvSecretFieldSchema = z.object({
  kind: z.literal(NeedsFieldKind.EnvSecret),
  ...vaultFieldMembers,
});
export const ServiceUrlFieldSchema = z.object({
  kind: z.literal(NeedsFieldKind.ServiceUrl),
  ...vaultFieldMembers,
});
export const McpServerFieldSchema = z.object({
  kind: z.literal(NeedsFieldKind.McpServer),
  name: RefNameSchema,
  /** Absent when the agent knows the server is needed but not where it lives — the human supplies it. */
  url: z.string().min(1).max(NEEDS_MCP_URL_MAX).optional(),
  transport: z.enum(NEEDS_MCP_TRANSPORTS).optional(),
  /** The NAME of the stored credential the server authenticates with — never the token. */
  token_ref: RefNameSchema.optional(),
});
export const OauthConnectionFieldSchema = z.object({
  kind: z.literal(NeedsFieldKind.OauthConnection),
  provider: z.enum(NEEDS_OAUTH_PROVIDERS),
  scopes: z
    .array(z.string().min(1).max(128).regex(/^\S+$/, 'a scope has no whitespace'))
    .min(1)
    .max(NEEDS_OAUTH_SCOPES_MAX)
    .optional(),
});

export const NeedsFieldSchema = z.discriminatedUnion('kind', [
  EnvSecretFieldSchema,
  ServiceUrlFieldSchema,
  McpServerFieldSchema,
  OauthConnectionFieldSchema,
]);
export type NeedsField = z.infer<typeof NeedsFieldSchema>;
export type EnvSecretField = z.infer<typeof EnvSecretFieldSchema>;
export type ServiceUrlField = z.infer<typeof ServiceUrlFieldSchema>;
export type McpServerField = z.infer<typeof McpServerFieldSchema>;
export type OauthConnectionField = z.infer<typeof OauthConnectionFieldSchema>;

/**
 * A field's NAME — its identity in the form, in `landed`, and in the fix link's
 * `fields_hash`. Total over {@link NeedsFieldKind}: a `switch` with no default, so
 * a fifth kind is a compile error here rather than a field with no name. See the
 * module header for why two kinds are bare and two are prefixed.
 */
export function needsFieldName(field: NeedsField): string {
  switch (field.kind) {
    case NeedsFieldKind.EnvSecret:
    case NeedsFieldKind.ServiceUrl:
      return field.name;
    case NeedsFieldKind.McpServer:
      return `${MCP_NAME_PREFIX}${field.name}`;
    case NeedsFieldKind.OauthConnection:
      return `${OAUTH_NAME_PREFIX}${field.provider}`;
  }
}

/** The two namespaces {@link needsFieldName} prefixes — one spelling, read by both directions. */
const MCP_NAME_PREFIX = 'mcp:';
const OAUTH_NAME_PREFIX = 'oauth:';

/**
 * Could `name` have come from {@link needsFieldName}? The reverse of it, over the same
 * three grammars ({@link RepoSecretNameSchema}, {@link RefNameSchema}, the OAuth
 * providers) rather than a fourth regex that can start disagreeing with them.
 *
 * It exists because a name does NOT always arrive from a parsed {@link NeedsField}. An
 * onboarding agent submits the access it asked for as free text, and those names are
 * stored on `repositories.ai_first_score` and printed in a pull request body on someone
 * else's repository — where `Closes #42` is a closing keyword and `@org/security` is a
 * ping. A model writes that text; this is the grammar that keeps it a NAME.
 */
export function isNeedsFieldName(name: string): boolean {
  if (name.startsWith(MCP_NAME_PREFIX)) {
    return RefNameSchema.safeParse(name.slice(MCP_NAME_PREFIX.length)).success;
  }
  if (name.startsWith(OAUTH_NAME_PREFIX)) {
    const provider: string = name.slice(OAUTH_NAME_PREFIX.length);
    return (NEEDS_OAUTH_PROVIDERS as readonly string[]).includes(provider);
  }
  return RepoSecretNameSchema.safeParse(name).success;
}

/** Every field's name, in field order. */
export function needsFieldNames(needs: { readonly fields: readonly NeedsField[] }): string[] {
  return needs.fields.map(needsFieldName);
}

/**
 * The self-consistency invariant, at BOTH boundaries: field names are unique, and
 * `landed` names only fields the form asked for, each at most once (write-once
 * per field). A row that breaks it contradicts itself — `landed` could not say
 * which of two same-named fields landed — so it is refused at the read too, the
 * posture `lifecycleRefinement` takes for the blocker's answer columns.
 */
function consistencyRefinement(
  needs: { fields: readonly NeedsField[]; landed: readonly string[] },
  ctx: z.RefinementCtx,
): void {
  const names = new Set<string>();
  for (const name of needsFieldNames(needs)) {
    if (names.has(name)) {
      ctx.addIssue({ code: 'custom', message: `duplicate field "${name}"`, path: ['fields'] });
    }
    names.add(name);
  }
  const landed = new Set<string>();
  for (const name of needs.landed) {
    if (!names.has(name)) {
      ctx.addIssue({
        code: 'custom',
        message: `landed names "${name}", which is not a field of this form`,
        path: ['landed'],
      });
    }
    if (landed.has(name)) {
      ctx.addIssue({ code: 'custom', message: `"${name}" landed twice`, path: ['landed'] });
    }
    landed.add(name);
  }
}

const needsMembers = {
  version: z.literal(BLOCKER_NEEDS_VERSION),
  fields: z.array(NeedsFieldSchema).min(1).max(BLOCKER_NEEDS_MAX_FIELDS),
  /** The NAMES ({@link needsFieldName}) of the fields a human has supplied. Never a value. */
  landed: z.array(z.string().min(1).max(200)).max(BLOCKER_NEEDS_MAX_FIELDS),
};

/**
 * A stored `blockers.needs`. Parsed on the way OUT of `@developerz/db` (it sits on
 * `BlockerSchema`), so it carries only the invariants a stored row must hold —
 * see {@link BlockerNeedsWriteSchema} for the ones only a write can.
 */
export const BlockerNeedsSchema = z.object(needsMembers).superRefine(consistencyRefinement);
export type BlockerNeeds = z.infer<typeof BlockerNeedsSchema>;

// ─── the WRITE seam ──────────────────────────────────────────────────────────

const askableVaultMembers = { environment: z.enum(BLOCKER_ASKABLE_ENVIRONMENTS) };

/** An MCP URL we would dial: `https:` (the policy's rule) and a publicly routable literal host. */
const McpServerFieldWriteSchema = McpServerFieldSchema.superRefine((field, ctx) => {
  if (field.url === undefined) return;
  const vetted = vetPublicHttpUrl(field.url, 'mcp_server url');
  if (!vetted.ok) {
    ctx.addIssue({ code: 'custom', message: vetted.reason, path: ['url'] });
    return;
  }
  if (vetted.url.protocol !== 'https:') {
    ctx.addIssue({ code: 'custom', message: 'mcp_server url must use https', path: ['url'] });
  }
});

const NeedsFieldWriteSchema = z.discriminatedUnion('kind', [
  EnvSecretFieldSchema.extend(askableVaultMembers),
  ServiceUrlFieldSchema.extend(askableVaultMembers),
  McpServerFieldWriteSchema,
  OauthConnectionFieldSchema,
]);

/**
 * {@link BlockerNeedsSchema} AT THE WRITE SEAM — the same shape plus the refusals
 * that only make sense before a row exists.
 *
 * DELIBERATELY NOT THE READ SHAPE. Folding these into the read would make an
 * already-stored row UNREADABLE, and `listBlockers` reads a whole page at once:
 * one bad row would fail the blocker list for the entire account. Narrowing a
 * read is data loss; narrowing a write is a gate.
 *
 * 1. **`prod` is refused** on both vault kinds — see {@link BLOCKER_ASKABLE_ENVIRONMENTS}.
 * 2. **`landed` must be empty**, and may be omitted (it parses to `[]`). `landed`
 *    is the SUBMIT's to write; a raiser that could set it could file an ask whose
 *    answer nobody gave.
 * 3. **An MCP `url` must be one the platform may dial** — `https:` and a publicly
 *    routable literal host ({@link vetPublicHttpUrl}), the same bar
 *    `.maintainer.yml`'s `McpUrlSchema` sets. Checked at write only: a host rule
 *    that tightens later must not make a stored row unreadable.
 * 4. **A credential-SHAPED string anywhere in the form is refused.** The name
 *    pattern bounds SHAPE, not secrecy: `ghp_AbCd…` is a legal env-var name. The
 *    residual is honest and pinned by `blocker.test.ts`: the scan catches known
 *    prefixes and a 32-char mixed-class run, and catches neither
 *    `sk_live_abcdef123` nor `hunter2`. So the column is NAMES-only by contract,
 *    not by detection.
 *
 * The refusal names the matched TAG and never the text: the rejected text is
 * precisely the thing that might be a secret, and a Zod issue carrying it would
 * put the leak into every log that prints the error.
 */
export const BlockerNeedsWriteSchema = z
  .object({
    ...needsMembers,
    fields: z.array(NeedsFieldWriteSchema).min(1).max(BLOCKER_NEEDS_MAX_FIELDS),
    landed: needsMembers.landed
      .max(0, 'a raiser cannot file a field as already landed — `landed` is written by the submit')
      .default([]),
  })
  .superRefine((needs, ctx) => {
    consistencyRefinement(needs, ctx);
    const shapes = secretShapesInJson(needs);
    if (shapes.length === 0) return;
    ctx.addIssue({
      code: 'custom',
      message: `refused: the form carries a credential-shaped string (${shapes.join(', ')}). \`needs\` carries NAMES only — a value written here is durable in the row, in every \`blocker list\`, and in the raw task an answer mints, and cannot be unsent.`,
      path: ['fields'],
    });
  });
/** What a raiser hands the write seam — `landed` may be omitted. */
export type BlockerNeedsDraft = z.input<typeof BlockerNeedsWriteSchema>;

/**
 * The scout's wire classification `{ env_keys, environment }` (`wire/scout-context.ts`
 * `BlockedNeedsSchema`, and what every runner build older than v2 sends on its
 * blocked signal) AS A FORM: one `env_secret` field per name. The ONE conversion,
 * so the mothership's ingest and any box-side raiser cannot disagree about it.
 *
 * Returns an UNVETTED value and decides nothing — typed with the wide
 * environment on purpose, so the caller has to parse it with
 * {@link BlockerNeedsWriteSchema}, which is where `prod`, a bad name and a
 * credential-shaped name are refused. This is a wire shape a live fleet still
 * speaks, not a read of stored v1 rows (there are none).
 */
export function envSecretNeeds(
  names: readonly string[],
  environment: SecretEnvironment,
): {
  version: typeof BLOCKER_NEEDS_VERSION;
  fields: { kind: typeof NeedsFieldKind.EnvSecret; name: string; environment: SecretEnvironment }[];
} {
  return {
    version: BLOCKER_NEEDS_VERSION,
    fields: names.map((name) => ({ kind: NeedsFieldKind.EnvSecret, name, environment })),
  };
}
